import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { useAuth } from '@/hooks/useAuth';
import { AssistantBriefButton } from './AssistantBriefButton';
import { resolveAssistantSuggestionKeys } from './assistantSuggestions';

interface AssistantEmptyStateProps {
  routeKey: string | null;
  onAsk: (question: string) => void;
}

function resolveFirstName(fullName: string): string {
  const firstName = fullName.trim().split(' ')[0];
  return firstName === undefined ? '' : firstName;
}

export function AssistantEmptyState({ routeKey, onAsk }: AssistantEmptyStateProps) {
  const { t } = useTranslation('assistant');
  const { currentUser } = useAuth();
  const firstName = currentUser === null ? '' : resolveFirstName(currentUser.fullName);
  return (
    <div className="flex-1 flex flex-col px-5 py-6">
      <div className="w-12 h-12 rounded-2xl bg-zooprimary text-zoowhite flex items-center justify-center text-xl mb-4 shadow-md">
        <Icon name="Sparkles" />
      </div>
      <h2 className="text-lg font-bold text-zooindigo">
        {firstName.length > 0
          ? t('empty.greetingWithName', { name: firstName })
          : t('empty.greeting')}
      </h2>
      <p className="text-sm text-zoomuted mt-1">{t('empty.subtitle')}</p>
      <AssistantBriefButton />
      <p className="text-[10px] font-bold uppercase tracking-wider text-zoomuted mt-6 mb-2">
        {t('empty.tryAsking')}
      </p>
      <div className="space-y-2">
        {resolveAssistantSuggestionKeys(routeKey).map(function renderSuggestion(suggestionKey) {
          const question = t('suggestions.' + suggestionKey);
          return (
            <button
              key={suggestionKey}
              type="button"
              onClick={function ask() {
                onAsk(question);
              }}
              className="group w-full text-left px-4 py-3 rounded-xl border border-zooborder bg-zoowhite text-sm text-zooindigo hover:border-zooprimary hover:text-zooprimary transition-colors flex items-center gap-3"
            >
              <Icon name="MessageSquare" className="text-zooviolet shrink-0" />
              <span className="flex-1">{question}</span>
              <Icon name="ArrowRight" className="text-zoolightlavender shrink-0" />
            </button>
          );
        })}
      </div>
      <p className="text-xs text-zoomuted mt-auto pt-6 flex items-start gap-2">
        <Icon name="Lock" className="shrink-0 mt-0.5" />
        {t('empty.privacy')}
      </p>
    </div>
  );
}
